/**
 * What the game sounds like, and when.
 *
 * `public/audio/` has been an empty placeholder since step 1 and STATE.md §7
 * has said "no audio" for sixty-one build steps. The reason it stayed easy to
 * add is that every system already announces itself: the train has fired
 * `approach`, `arrive` and `depart` through `onCue` since step 33 with nothing
 * but a dev log on the end of it, and blows, heat, phases and state changes all
 * have listeners on them for the overlay's benefit. **This module subscribes to
 * hooks that already existed and adds none.** Nothing anywhere else in the
 * project learned that audio exists — `main.js` constructs it and hands it the
 * graph, and that is the whole of the integration.
 *
 * Three things are worth knowing about the shape.
 *
 * **It is pure, and the browser half is `audioDevice.js`.** This decides *what*
 * plays — which sound a blow makes, whether a cooldown allows it, what gain and
 * playback rate it gets, and how loud the crowd is right now. It has never
 * heard of an `AudioContext`. A `device` is injected, defaults to nothing, and
 * a null device is a silent game rather than a broken one — which is what the
 * 1502 unit tests run against. Same split as `inputMap.js`/`input.js` and
 * `save.js`/`saveStore.js`.
 *
 * **The crowd is a bed, not a sound.** It is a level driven by
 * `crowd.energy` — the curve from step 37, already shaped flat-then-steep
 * because a crowd does not warm up gradually — smoothed with separate rise and
 * fall constants, because a room comes up faster than it goes down. Nothing
 * about it is a one-shot.
 *
 * **Everything is data.** `audio.json` names each sound, the voice that
 * synthesises it, its gain, its decay, how much its pitch varies per play, and
 * what cooldown it sits behind. A sound that grows a `src` is loaded and played
 * as a file instead, which is the entire drop-in path for real recordings: a
 * data change, not a code change.
 */

import AUDIO from './audio.json';

export class AudioConfigError extends Error {}

/**
 * The flags a blow can be sorted by, and how each is read off one.
 *
 * Deliberately small. An impact rule names one of these or is the catch-all,
 * and the ordering in the file is the precedence — a train before a pit before
 * a weapon before a wall — because that ordering *is* the rule and deriving it
 * from damage numbers would hide it.
 */
export const BLOW_TESTS = Object.freeze({
  lethal: (hit) => Boolean(hit?.lethal),
  pit: (hit) => Boolean(hit?.pit),
  weapon: (hit) => Boolean(hit?.weapon),
  environmental: (hit) => Boolean(hit?.environmental),
  blocked: (hit) => Boolean(hit?.blocked),
  strong: (hit) => hit?.strength === 'strong',
  counter: (hit) => Boolean(hit?.counter),
});

/** The synthesis recipes `audioDevice.js` knows how to build. */
export const VOICES = Object.freeze(['thud', 'click', 'clang', 'noise', 'tone', 'sweep', 'bed']);

/**
 * Throws on a table that could not mean what it says.
 *
 * The interesting refusals are the ones about *meaning*: a cue pointing at a
 * sound nobody defined is silence somebody thinks they configured, an impact
 * list with no catch-all is a blow that makes no noise, and a rule after the
 * catch-all can never fire — the same rule `lines.js` enforces on a script,
 * for the same reason.
 */
export function validateAudio(config = AUDIO) {
  const { master, sounds, impacts, groups, bed } = config ?? {};

  if (!(master?.volume >= 0 && master.volume <= 1)) {
    throw new AudioConfigError(`master.volume is ${JSON.stringify(master?.volume)}; want 0..1`);
  }
  if (typeof master.muted !== 'boolean') {
    throw new AudioConfigError(`master.muted is ${JSON.stringify(master.muted)}`);
  }

  if (!sounds || !Object.keys(sounds).length) throw new AudioConfigError('there are no sounds');
  for (const [name, sound] of Object.entries(sounds)) {
    if (!VOICES.includes(sound.voice)) {
      throw new AudioConfigError(`"${name}" is a "${sound.voice}"; there is ${VOICES.join(', ')}`);
    }
    if (!(sound.gain > 0)) throw new AudioConfigError(`"${name}" has a gain of ${sound.gain}`);
    if (!(sound.decay > 0)) throw new AudioConfigError(`"${name}" has a decay of ${sound.decay}`);
    // A pitch variance of zero is legal and means "always identical", which is
    // right for a bell and wrong for a punch. Anything at or past 1 would let a
    // playback rate reach zero, which is a sound that never ends.
    if (!(sound.pitch >= 0 && sound.pitch < 1)) {
      throw new AudioConfigError(`"${name}" varies pitch by ${sound.pitch}; want 0..1`);
    }
    if (sound.group && !groups?.[sound.group]) {
      throw new AudioConfigError(`"${name}" is in group "${sound.group}", which is not a group`);
    }
    if (sound.noise !== undefined && !(sound.noise >= 0 && sound.noise <= 1)) {
      throw new AudioConfigError(`"${name}" mixes ${sound.noise} noise; want 0..1`);
    }
    const hz = sound.hz;
    const ends = Array.isArray(hz) ? hz : [hz];
    if (!ends.length || ends.some((one) => !(one > 0))) {
      throw new AudioConfigError(`"${name}" is at ${JSON.stringify(hz)} Hz`);
    }
    if (sound.voice === 'sweep' && !Array.isArray(hz)) {
      throw new AudioConfigError(`"${name}" is a sweep and has one frequency; want [from, to]`);
    }
  }

  for (const [name, group] of Object.entries(groups ?? {})) {
    if (!(group?.cooldown >= 0)) {
      throw new AudioConfigError(`group "${name}" has a cooldown of ${group?.cooldown}`);
    }
  }

  if (!Array.isArray(impacts) || !impacts.length) {
    throw new AudioConfigError('nothing says what a blow sounds like');
  }
  let closed = false;
  for (const rule of impacts) {
    if (closed) {
      throw new AudioConfigError(`"${rule.sound}" comes after the catch-all and can never fire`);
    }
    if (rule.when === null || rule.when === undefined) closed = true;
    else if (!BLOW_TESTS[rule.when]) {
      throw new AudioConfigError(
        `an impact keys on "${rule.when}"; there is ${Object.keys(BLOW_TESTS).join(', ')}`
      );
    }
    if (!sounds[rule.sound]) {
      throw new AudioConfigError(`an impact plays "${rule.sound}", which is not a sound`);
    }
  }
  // Without one, an ordinary punch is silent and the file looks complete.
  if (!closed) throw new AudioConfigError('the impact list has no catch-all; a plain blow would be silent');

  // Every other table is a plain lookup into `sounds`, and a name that is not
  // there is silence somebody thinks they configured.
  for (const table of ['states', 'phases', 'heat', 'train', 'weapons', 'crowd']) {
    for (const [key, name] of Object.entries(config[table] ?? {})) {
      if (!sounds[name]) {
        throw new AudioConfigError(`${table}.${key} plays "${name}", which is not a sound`);
      }
    }
  }

  if (!(bed?.gain > 0)) throw new AudioConfigError(`the bed has a gain of ${bed?.gain}`);
  if (!(bed.floor >= 0 && bed.floor < bed.gain)) {
    throw new AudioConfigError(`the bed's floor is ${bed.floor}; it has to sit under its gain`);
  }
  // A room comes up faster than it goes down. The other way round is a crowd
  // that hangs on after a fight has gone quiet, which reads as a stuck sound.
  if (!(bed.rise > 0) || !(bed.fall > 0)) {
    throw new AudioConfigError('the bed needs a positive rise and fall');
  }
  if (!(bed.fall > bed.rise)) {
    throw new AudioConfigError(`the bed falls (${bed.fall}s) faster than it rises (${bed.rise}s)`);
  }

  return config;
}

validateAudio(AUDIO);

export { AUDIO };

/**
 * Which sound a blow makes.
 *
 * First match wins down the file's own order, which is why a weapon through a
 * wall is a weapon and a body under a train is a train.
 */
export function impactFor(hit, config = AUDIO) {
  for (const rule of config.impacts) {
    if (rule.when === null || rule.when === undefined) return rule.sound;
    if (BLOW_TESTS[rule.when]?.(hit)) return rule.sound;
  }
  return null;
}

/** Smooths `level` toward `want` over a time constant, frame-rate independent. */
export function approach(level, want, tau, dt) {
  if (!(tau > 0) || !(dt > 0)) return want;
  return level + (want - level) * (1 - Math.exp(-dt / tau));
}

export function createAudio({
  /**
   * The thing that actually makes a noise. Null is a silent game rather than a
   * broken one, which is what every unit test runs against and what a browser
   * with the audio context blocked degrades to.
   */
  device = null,
  config = AUDIO,
  random = Math.random,
  ...sources
} = {}) {
  validateAudio(config);

  let volume = config.master.volume;
  let muted = config.master.muted;
  /** Its own clock, advanced by `update`. Cooldowns are measured on it rather
   *  than on wall time, so a headless run at a thousand frames a second gates
   *  exactly as the game does. */
  let clock = 0;
  /** The crowd, as a level rather than as a sound. */
  let bedLevel = 0;
  const lastPlayed = new Map();
  const lastGroup = new Map();
  /** A rolling list of what has been played, for the overlay and the tests. */
  let recent = [];
  const unsubscribe = [];
  /** The current arena's train, which is the one subscription that gets swapped. */
  let offTrain = null;

  const soundOf = (name) => config.sounds[name] ?? null;

  /** Whether `name` may be heard again yet, on its own gate and its group's. */
  function ready(name) {
    const sound = soundOf(name);
    if (!sound) return false;
    if (clock - (lastPlayed.get(name) ?? -Infinity) < (sound.cooldown ?? 0)) return false;
    const group = sound.group ? config.groups[sound.group] : null;
    if (group && clock - (lastGroup.get(sound.group) ?? -Infinity) < group.cooldown) return false;
    return true;
  }

  /**
   * Plays one sound, or decides not to.
   *
   * The mute is checked **before** the cooldowns, so a muted stretch does not
   * silently spend them — unmuting mid-round should not be followed by a second
   * of nothing while gates that were never heard expire.
   *
   * `pitch` is rolled here rather than in the device, because "the same punch
   * twice must not sound identical" is a rule and the device is a synthesiser.
   */
  function play(name, { gain: scale = 1 } = {}) {
    const sound = soundOf(name);
    if (!sound || muted || volume <= 0) return null;
    if (!ready(name)) return null;

    lastPlayed.set(name, clock);
    if (sound.group) lastGroup.set(sound.group, clock);

    const spread = sound.pitch ?? 0;
    const cue = Object.freeze({
      name,
      sound,
      gain: sound.gain * scale * volume,
      /** Never at or below zero: `pitch` is validated under 1. */
      rate: 1 + (random() * 2 - 1) * spread,
      at: clock,
    });
    recent = [cue, ...recent].slice(0, 12);
    device?.play?.(cue);
    return cue;
  }

  /** How loud the room should be right now, with the master applied. */
  function bedGain() {
    if (muted) return 0;
    const { floor, gain } = config.bed;
    return (floor + (gain - floor) * bedLevel) * volume;
  }

  const audio = {
    config,
    get volume() {
      return volume;
    },
    setVolume(next) {
      volume = Math.min(1, Math.max(0, Number(next) || 0));
      device?.bed?.(bedGain(), config.bed);
      return volume;
    },
    get muted() {
      return muted;
    },
    setMuted(next) {
      muted = Boolean(next);
      device?.bed?.(bedGain(), config.bed);
      if (muted) device?.silence?.();
      return muted;
    },
    toggle() {
      return audio.setMuted(!muted);
    },

    /** Where the crowd bed is, 0..1, before the master is applied. */
    get crowd() {
      return bedLevel;
    },
    /** The last dozen cues, newest first. For the overlay and the tests. */
    get recent() {
      return recent;
    },
    /** Whether anything is actually going to come out. */
    get audible() {
      return Boolean(device) && !muted && volume > 0;
    },
    /**
     * The browser half, or null. Exposed for the overlay's voice count and for
     * the headless check that the synthesiser is really building nodes — the
     * rules can be proved with a recording stub, and that is the one thing it
     * cannot prove.
     */
    get device() {
      return device;
    },

    play,

    /**
     * One frame. The only thing here that is not event-driven is the crowd,
     * because a crowd is not an event.
     */
    update(dt) {
      if (!(dt > 0)) return bedLevel;
      clock += dt;
      const want = sources.crowd ? sources.crowd.energy : 0;
      // Up fast, down slow. A room that faded as quickly as it filled reads as
      // a sound that got cut off.
      const tau = want > bedLevel ? config.bed.rise : config.bed.fall;
      bedLevel = approach(bedLevel, want, tau, dt);
      device?.bed?.(bedGain(), config.bed);
      return bedLevel;
    },

    /**
     * The arena changed, so the train did.
     *
     * Called from `createArenaHost`'s `onChange`, which is where everything an
     * arena decides for somebody else is re-pointed (the same place step 61 put
     * `setArena`). The old subscription goes first: a disposed arena's train
     * still holding a listener is a departure announced by a service that is no
     * longer in the scene.
     */
    arena(next) {
      offTrain?.();
      offTrain = null;
      const cues = config.train ?? {};
      offTrain =
        next?.train?.onCue?.(({ cue }) => {
          if (cues[cue]) play(cues[cue]);
        }) ?? null;
      return Boolean(offTrain);
    },

    /** The master, as a career carries it. Not what a career *did*, but the
     *  only slot this game has — see the note in `save.js`. */
    snapshot() {
      return { volume, muted };
    },
    restore(saved) {
      if (!saved || typeof saved !== 'object') return false;
      audio.setVolume(saved.volume ?? volume);
      audio.setMuted(saved.muted ?? muted);
      return true;
    },
    reset() {
      audio.setVolume(config.master.volume);
      audio.setMuted(config.master.muted);
      return audio;
    },

    dispose() {
      for (const off of unsubscribe.splice(0)) off?.();
      offTrain?.();
      offTrain = null;
      device?.dispose?.();
    },
  };

  // --- everything it listens to ---------------------------------------------
  //
  // Every one of these existed before this module did. Nothing was added to a
  // rule module to make a noise possible, which is the property worth keeping:
  // a system that has to be told about audio is a system that can forget.

  const { fight, match, crowd, fighters = [], ground } = sources;

  if (fight) {
    // A blow, of any kind. The wall, the pit and the train all come through
    // here too — `strikeSurface` and `fallIntoPit` both go through `land`, so
    // subscribing to `onSurface` as well would play two sounds for one impact.
    unsubscribe.push(fight.onHit?.((hit) => play(impactFor(hit, config))) ?? null);
    unsubscribe.push(fight.onKnockout?.(() => play(config.states?.dead ?? 'knockout')) ?? null);
    // A weapon changing hands is its own small noise, and it is not a hit.
    if (config.weapons?.reversal) {
      unsubscribe.push(fight.onReversal?.(() => play(config.weapons.reversal)) ?? null);
    }
  }

  if (match && config.phases) {
    unsubscribe.push(
      match.onPhase?.(({ to }) => {
        if (config.phases[to]) play(config.phases[to]);
      }) ?? null
    );
  }

  if (crowd) {
    if (config.crowd?.grab) {
      // Fires on the reach *and* on the let go; only the reach is a noise.
      unsubscribe.push(crowd.onGrab?.(({ held }) => held && play(config.crowd.grab)) ?? null);
    }
    if (config.weapons?.offer) {
      unsubscribe.push(crowd.onOffer?.(() => play(config.weapons.offer)) ?? null);
    }
  }

  if (ground && config.weapons?.pickup) {
    unsubscribe.push(ground.onPickup?.(() => play(config.weapons.pickup)) ?? null);
  }

  for (const who of fighters) {
    // A body arriving somewhere. Most states are silent; the table says which.
    if (config.states) {
      unsubscribe.push(
        who?.machine?.onChange?.(({ to }) => {
          if (config.states[to]) play(config.states[to]);
        }) ?? null
      );
    }
    // A swing through air, which is the only thing the strike layer announces
    // that the fight does not.
    if (config.sounds.whiff) {
      unsubscribe.push(who?.strikes?.onWhiff?.(() => play('whiff')) ?? null);
    }
    // Heat moves in two directions and the meter already says which.
    if (config.heat) {
      unsubscribe.push(
        who?.heat?.onEvent?.((event) => {
          const name = event.rise ? config.heat.rise : config.heat.fall;
          if (name) play(name);
        }) ?? null
      );
    }
  }

  return audio;
}
